import { useState } from 'react';
import { S } from '../../domain/state.js';
import { fmt, fmtSigned, fMoney, wD, todayISO, iso10, dDays, DAYS, MONTHS } from '../../lib/format.js';
import { curKey, keyAdd, periodStart, periodShort } from '../../domain/periods.js';
import { filterActive, sumBy, findDupTx } from '../../domain/tx.js';
import { cat } from '../../domain/categories.js';
import { periodOpen, periodFlow, balEst, periodBalKnown } from '../../domain/balance.js';
import { futurePeriodForecast } from '../../domain/forecast.js';
import { flowAlert, periodPace, weeklyTasks } from '../../domain/plan.js';
import { findDupRec } from '../../domain/recurring.js';
import { scanCoverage } from '../../domain/coverage.js';
import { revCandidates, unclassifiedMerchants } from '../../domain/merchants.js';
import { cardChargeTxt } from '../../domain/explain.js';
import { Xn, Sheet, openWhy, confirmDialog } from '../../components/ui.jsx';
import { navigate, openModal } from '../../app/nav.js';
import { UI, useApp, ls, toast, setTxf } from '../../store/ui.js';
import { approveDups, approveAllDups, deleteTx } from '../../store/actions.js';
import { exportBackup } from '../settings/backup.js';
import { HomeHero, AffordButton } from './Hero.jsx';
import { ExitPlan } from './ExitPlan.jsx';
import { Breakdown } from './Breakdown.jsx';
import { openClassify } from './Classify.jsx';
import { openScan } from '../add/AddPage.jsx';
import { openRecModal } from '../settings/RecModal.jsx';
import { openReview, ReviewBody } from '../reports/Review.jsx';
import { pendingReviewKey, catSpikes } from '../../domain/review.js';
import { txfDefault } from '../../domain/txFilter.js';
import { Icon } from '../../components/Icon.jsx';

export function Dashboard() {
  useApp();
  const key = UI.viewKey, today = curKey();
  const isCur = key === today, isFuture = key > today;
  const txs = S.tx.filter((t) => t.month === key);
  const act = filterActive(txs);
  const inc = sumBy(act, 'income'), exp = sumBy(act, 'expense');
  const showHero = isCur && !!S.set.balDate;
  const o = (n) => ({ order: n });
  return (
    <div className="stack">
      <Readiness />
      {!showHero && <FlowAlertBox />}
      {showHero && <HomeHero />}
      {showHero && <AffordButton />}
      <Attention isCur={isCur} />
      <div className="cols interleave">
        <div className="col">
          {!showHero && <div style={o(1)}><SummaryCards k={key} txs={txs} inc={inc} exp={exp} isFuture={isFuture} /></div>}
          {!showHero && isCur && <div style={o(2)}><ForecastKpi k={key} exp={exp} isCur={isCur} /></div>}
          {!isCur && !isFuture && <div className="panel" style={o(2)}><div className="sec">סיכום התקופה</div><ReviewBody k={key} /></div>}
          <div style={o(4)}>{isFuture ? <FuturePanel FP={futurePeriodForecast(key)} /> : <Breakdown />}</div>
        </div>
        <div className="col">
          {isCur && <div style={o(3)}><ExitPlan /></div>}
        </div>
      </div>
    </div>
  );
}

/* ── צעדים ראשונים: מה צריך כדי שהתחזית תעבוד. נעלם כשהכל מוכן ── */
function Readiness() {
  const openSet = (g) => { ls.set('set_open', g); navigate('Set'); };
  const steps = [
    { done: !!S.set.balDate, t: 'יתרה בעו"ש', d: 'כמה יש היום בחשבון. פעם אחת, ומשם היא מתעדכנת לבד מסריקות העו"ש', btn: 'הזנת יתרה', go: () => openSet('bal') },
    { done: S.tx.length > 0, t: 'סריקת דוח ראשון', d: 'קובץ Excel מהבנק או מחברת האשראי. הפעולות נכנסות ומסווגות לבד', btn: 'לסריקה', go: openScan },
    { done: S.rec.some((r) => r.type === 'income'), t: 'משכורת וחיובים קבועים', d: 'משכורת, שכר דירה, משכנתא. מהם בנויה התחזית קדימה', btn: 'הוספה', go: () => openRecModal() },
  ];
  const left = steps.filter((s) => !s.done).length;
  const dupRec = findDupRec().length > 0;
  if (!left && !dupRec) return null;
  if (!left) return (
    <div className="alert-gold">
      <div className="alert-title"><Icon name="alert" size={18} />יש חיובים קבועים כפולים, ולכן התחזית מנופחת</div>
      <button className="ubtn ubtn-outline btn-sm" onClick={() => navigate('Flow')}>לתיקון בתזרים</button>
    </div>
  );
  return (
    <div className="period onboard">
      <div className="card-h">
        <div className="sec" style={{ margin: 0 }}>{S.tx.length ? 'כמה צעדים כדי שהתחזית תהיה מדויקת' : 'ברוכים הבאים! 3 צעדים להתחלה'}</div>
        <span className="sec-sub">{steps.length - left} מתוך {steps.length}</span>
      </div>
      {steps.map((s, i) => (
        <div key={i} className={'ob-step' + (s.done ? ' done' : '')}>
          <span className="ob-num">{s.done ? <Icon name="check" size={16} /> : i + 1}</span>
          <div className="grow"><div className="ob-t">{s.t}</div>{!s.done && <div className="note">{s.d}</div>}</div>
          {!s.done && <button className="ubtn ubtn-primary btn-sm" onClick={s.go}>{s.btn}</button>}
        </div>
      ))}
    </div>
  );
}

/* ── כפילויות ודאיות: זהות בכל השדות. נפתח מ"דורש טיפול" ── */
function DupSheet() {
  useApp();
  const d = findDupTx();
  const sum = d.reduce((s, x) => s + Math.abs(Number(x.extra.amount) || 0), 0);
  async function del(t) {
    if (!(await confirmDialog({ title: 'מחיקת פעולה', body: '"' + t.description + '" ' + fmt(t.amount) + ' מ-' + t.date, okLabel: 'מחק', danger: true }))) return;
    try { await deleteTx(t.id); toast('🗑️ נמחקה'); } catch (e) { toast('שגיאה: ' + e.message); }
  }
  async function okAll() {
    if (await confirmDialog({ title: 'אישור כפילויות', body: 'לאשר את כל ' + d.length + ' העסקאות כנפרדות? הן לא יופיעו שוב.' })) approveAllDups();
  }
  return (
    <Sheet title="עסקאות כפולות">
      {!d.length ? <div className="empty-s">✓ אין יותר כפילויות</div> : <>
        <div className="sub mb12">זהות לחלוטין בתיאור, בסכום ובתאריך, ומוסיפות {fmt(sum)} לסיכומים. לפעמים זה אמיתי (שתי קניות זהות באותו יום), ולפעמים אותה עסקה נשמרה פעמיים.</div>
        {d.slice(0, 30).map((x) => (
          <div key={x.extra.id} className="list-row">
            <div className="grow"><div className="ellipsis" style={{ fontWeight: 600 }}>{x.extra.description}</div><div className="note">{x.extra.date} · {fmt(x.extra.amount)}</div></div>
            <button className="ubtn ubtn-outline btn-xs" onClick={() => approveDups([x.extra.id])}>תקין</button>
            <button className="ubtn ubtn-ghost btn-xs danger-text" onClick={() => del(x.extra)}>מחק</button>
          </div>
        ))}
        {d.length > 1 && <button className="ubtn ubtn-outline w100 mt12" onClick={okAll}>כולן עסקאות נפרדות</button>}
      </>}
    </Sheet>
  );
}

/* ── התראה: התחזית נוגעת במסגרת (בתקופות שאינן הנוכחית) ── */
function FlowAlertBox() {
  const a = flowAlert();
  if (!a) return null;
  const { low, lim, over } = a;
  return (
    <div style={{ borderRadius: 12, padding: '13px 15px', marginBottom: 12, boxShadow: 'var(--shadow)', background: over ? 'var(--red-l)' : 'var(--gold-l)', borderInlineStart: '4px solid ' + (over ? 'var(--red)' : 'var(--gold)') }}>
      <div className="alert-title">{over ? '🔴 התחזית חורגת מהמסגרת' : '🟡 התחזית מתקרבת לגבול המסגרת'}</div>
      <div className="sub" style={{ fontSize: '.86rem' }}>
        נקודת שפל {fMoney(low.balance)} ביום {DAYS[low.date.getDay()]}, {low.date.getDate()} ב{MONTHS[low.date.getMonth()]}
        {over ? '. חריגה של ' + fMoney(Math.abs(-lim - low.balance)) : ' מתוך מסגרת ' + fMoney(lim)}.
      </div>
      <button className="ubtn ubtn-outline ubtn-sm-pad mt6" onClick={() => navigate('Flow')}>לפרטים</button>
    </div>
  );
}

/* ── דורש טיפול: כפילויות, משימות השבוע וגיבוי, ברשימה אחת. נעלם כשאין כלום ── */
function Attention({ isCur }) {
  const [, force] = useState(0);
  const items = [];
  // תחילת תקופה: סיכום של התקופה שהסתיימה, פעם אחת
  const rk = isCur ? pendingReviewKey(ls.get('review_seen', ''), dDays(todayISO(), periodStart(curKey()))) : null;
  if (rk) items.push({ icon: '📊', t: 'סיכום ' + periodShort(rk), d: 'התקופה הסתיימה. כמה נכנס, כמה יצא, ומה בלט', go: () => { ls.set('review_seen', rk); openReview(rk); force((x) => x + 1); } });
  const d = findDupTx();
  if (d.length) {
    const sum = d.reduce((s, x) => s + Math.abs(Number(x.extra.amount) || 0), 0);
    items.push({ icon: '🔎', t: d.length === 1 ? 'עסקה כפולה אחת' : d.length + ' עסקאות כפולות', d: 'אותו שם, סכום ותאריך. מוסיפות ' + fmt(sum) + ' לסיכומים', go: () => openModal(() => <DupSheet />) });
  }
  if (isCur) {
    // במקום יעדי תקציב ידניים: קטגוריה שיצא בה הרבה יותר מהרגיל עד אותו יום בתקופה
    catSpikes().forEach((x) => {
      const c = cat('expense', x.c);
      items.push({
        icon: c.icon, t: c.label + ': יותר מהרגיל', urgent: true,
        d: fMoney(x.v) + ' עד היום. בדרך כלל עד שלב כזה ' + fMoney(x.usual) + (x.usual > 0 ? ' (+' + Math.round((x.v / x.usual - 1) * 100) + '%)' : ''),
        go: () => { setTxf({ ...txfDefault(), sort: 'amount', cats: ['expense:' + x.c] }); navigate('Tx'); },
      });
    });
    const rev = revCandidates();
    const go = { scan: openScan, revfix: () => openWhy('revfix'), recmiss: () => openWhy('recmiss'), goals: () => navigate('Goals'), classify: openClassify };
    weeklyTasks({
      coverage: scanCoverage(),
      revCount: rev.length,
      revSample: rev.slice(0, 2).map((x) => x.t.description + ' ← ' + x.rev).join(', '),
      unclassified: unclassifiedMerchants(UI.viewKey),
    }).forEach((x) => items.push({ icon: x.icon, t: x.t, d: x.d, urgent: x.urgent, go: go[x.go] }));
  }
  const last = ls.get('last_backup', '');
  const days = last ? Math.floor((Date.now() - new Date(last).getTime()) / 86400000) : 999;
  if (S.tx.length >= 10 && days >= 7) {
    items.push({ icon: '💾', t: 'גיבוי', d: (last ? 'הגיבוי האחרון לפני ' + days + ' ימים' : 'עדיין לא גיבית') + '. לחיצה מורידה קובץ גיבוי', go: async () => { await exportBackup(); force((x) => x + 1); } });
  }
  if (!items.length) return null;
  return (
    <div className="period">
      <div className="sec">דורש טיפול <span className="sec-sub">{items.length}</span></div>
      <div className="tasks">
        {items.map((x, i) => (
          <div key={i} className="task" onClick={x.go} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && x.go()}>
            <span className="task-ico">{x.icon}</span>
            <div className="grow"><div style={{ fontSize: '.95rem', fontWeight: 700 }}>{x.t}</div><div style={{ fontSize: '.86rem', lineHeight: 1.5, marginTop: 2, color: x.urgent ? 'var(--red)' : 'var(--ink3)' }}>{x.d}</div></div>
            <Icon name="chevL" size={16} style={{ color: 'var(--ink3)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── סיכום תקופה: תקופות קודמות, עתידיות, או נוכחית בלי יתרה ── */
function SummaryCards({ k, txs, inc, exp, isFuture }) {
  const FP = isFuture ? futurePeriodForecast(k) : null;
  const farFut = isFuture && !FP;
  if (FP) { inc = FP.inc; exp = FP.exp; }
  const isCur = k === curKey();
  let bal = inc - exp, balLbl = bal > 0 ? 'חיסכון 🎉' : bal < 0 ? 'גירעון ⚠️' : 'מאוזן', rows = null, title = 'יתרה';
  if (S.set.balDate && !isFuture && !periodBalKnown(k)) {
    title = 'יתרה בעו"ש';
    balLbl = 'אין נתוני עו"ש לתקופה הזו. היתרה ידועה רק מ-' + wD(S.set.balDate) + ', נקודת הפתיחה, או מהסריקה הראשונה של העו"ש.';
    bal = null;
  } else if (S.set.balDate && !isFuture) {
    const open = periodOpen(k), BF = periodFlow(k, isCur ? todayISO() : null);
    const close = isCur ? balEst().amount : periodOpen(keyAdd(k, 1));
    bal = close;
    title = 'יתרה בעו"ש בסוף התקופה';
    rows = [['פתיחה (' + wD(periodStart(k)) + ')', fmtSigned(open)], ['נכנס לעו"ש', fmt(BF.inc, '+')], ['יצא מהעו"ש' + (BF.card ? ' (בלי חיוב הכרטיס)' : ''), fmt(BF.exp, '-')]];
    if (BF.card) rows.push([cardChargeTxt(BF), fmt(BF.card, '-')]);
    if (BF.late) rows.push(['קרה ועוד לא נסרק', fmt(BF.late, BF.late < 0 ? '-' : '+')]);
    rows.push([isCur ? 'היום' : 'סגירה = פתיחת התקופה הבאה', fmtSigned(close)]);
  } else if (FP) {
    if (FP.hasBal) {
      bal = FP.close;
      title = 'יתרה סגירה (צפוי)';
      rows = [['פתיחה', fmtSigned(FP.open)], ['הכנסות', fmt(FP.inc, '+')], ['הוצאות', fmt(FP.exp, '-')]];
      if (FP.low < Math.min(FP.open, FP.close) - 1) rows.push(['שפל ב-' + wD(FP.lowDate), fmtSigned(FP.low)]);
      rows.push(['סגירה', fmtSigned(FP.close)]);
    } else { title = 'יתרה (צפוי)'; balLbl = 'חסרה יתרת עו"ש, ולכן אין יתרה צפויה'; }
  } else if (farFut) balLbl = 'רחוק מדי לתחזית';
  const dashed = isFuture ? { border: '1px dashed var(--gold)' } : undefined;
  const fc = isFuture && <div style={{ fontSize: '.82rem', color: 'var(--gold)', fontWeight: 700, marginTop: -6, marginBottom: 2 }}>~ תחזית ~</div>;
  const amt = (v, n) => (farFut ? '-' : FP ? <Xn k="fut">{n}</Xn> : n);
  const nInc = txs.filter((t) => t.type === 'income').length, nExp = txs.filter((t) => t.type === 'expense').length;
  return (
    <div className="cards">
      <div className="card inc" style={dashed}><div className="clbl">הכנסות</div>{fc}<div className="camt">{amt(inc, fmt(inc))}</div>
        <div className="csub">{farFut ? '' : FP ? (FP.nInc ? FP.nInc + ' הכנסות קבועות' : 'אין הכנסות קבועות מוגדרות') : nInc + ' פעולות'}</div></div>
      <div className="card exp" style={dashed}><div className="clbl">הוצאות</div>{fc}<div className="camt">{amt(exp, fmt(exp))}</div>
        <div className="csub">{farFut ? '' : FP ? (FP.card ? 'כולל אשראי ~' + fmt(FP.card) : FP.nExp + ' חיובים') : nExp + ' פעולות'}</div></div>
      <div className="card bal" style={{ ...dashed, cursor: FP ? 'pointer' : undefined }} onClick={FP ? () => openWhy('fut') : undefined}>
        <div className="clbl">{title}</div>
        <div className="camt" style={{ color: bal >= 0 ? 'var(--green)' : 'var(--red)' }}>{farFut || bal == null ? '-' : amt(bal, fmt(bal, bal < 0 ? '-' : ''))}</div>
        <div className="csub">{rows ? <div className="bal-rows">{rows.map(([n, v], i) => <div key={i} className={'bal-row' + (i === rows.length - 1 ? ' tot' : '')}><span>{n}</span><b dir="ltr">{v}</b></div>)}</div> : balLbl}</div>
      </div>
    </div>
  );
}

/* ── הוצאות בתקופה, בהשוואה לאותו יום בתקופות קודמות ── */
function ForecastKpi({ k, exp, isCur }) {
  const pace = periodPace(k, exp);
  if (!isCur) return (
    <div className="kpi-row"><div className="kpi"><div className="klbl">הוצאות בתקופה</div><div className="famt">{fmt(exp)}</div>
      <div className="ksub">{pace.typical ? 'מחזור טיפוסי ' + fmt(pace.typical) : 'מחזור סגור'}</div></div></div>
  );
  // מתי עודכן לאחרונה: ההבדל בין "הוצאנו פחות" לבין "לא הזנו עדיין"
  let newest = '';
  S.tx.forEach((t) => { if (t.month === k) { const d = iso10(t.created_at || t.date); if (d > newest) newest = d; } });
  const days = newest ? dDays(todayISO(), newest) : null;
  const stale = newest ? (days <= 0 ? 'עודכן היום' : days === 1 ? 'עודכן אתמול' : 'עודכן לפני ' + days + ' ימים') : 'אין נתונים במחזור';
  const line1 = ['יום ' + pace.day + '/' + pace.total], line2 = [];
  if (pace.ok) { line1.push('רגיל עד שלב כזה ' + fmt(pace.usual) + ' (' + (pace.pct > 0 ? '+' : '') + pace.pct + '%)'); line2.push('צפי לסוף התקופה ~' + fmt(pace.proj)); }
  else if (pace.typical) line1.push('מחזור טיפוסי ' + fmt(pace.typical));
  line2.push(stale);
  const ratio = pace.usual > 0 ? exp / pace.usual : pace.typical > 0 ? exp / pace.typical : 0;
  return (
    <div className="kpi-row"><div className="kpi">
      <div className="klbl">נרשם במחזור</div><div className="famt">{fmt(exp)}</div>
      <div className="ksub">{line1.join(' · ')}<br />{line2.join(' · ')}</div>
      <div className="fbar"><div className="ffill" style={{ width: Math.min(Math.round(ratio * 100), 100) + '%', background: ratio > 1.15 ? 'var(--red)' : ratio > 1 ? 'var(--gold)' : 'var(--green)' }} /></div>
    </div></div>
  );
}

/* ── תקופה עתידית: מה צפוי לזוז בעו"ש ── */
function FuturePanel({ FP }) {
  return (
    <div className="panel">
      <div className="sec">מה צפוי בתקופה</div>
      {!FP ? <div className="empty-s">התקופה רחוקה מדי בשביל תחזית מועילה.<br />התזרים מחשב עד שנה קדימה.</div> : (
        <>
          <div className="sub mb8">תנועות צפויות בעו"ש לפי החיובים הקבועים וחיוב האשראי המשוער. עסקאות חדשות באשראי ייכנסו לחיוב של החודש שאחרי.</div>
          {!FP.items.length ? <div className="empty-s">אין חיובים צפויים בתקופה. כדאי להגדיר חיובים קבועים.</div>
            : FP.items.slice().sort((a, b) => a.date - b.date).map((it, i) => (
              <div key={i} className="row-b" style={{ padding: '8px 2px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ minWidth: 0 }}><div className="ellipsis" style={{ fontSize: '.93rem', fontWeight: 700 }}>{it.name}</div>
                  <div className="note">{wD(it.date)}{it.est ? ' · הערכה לפי תקופות קודמות' : ''}</div></div>
                <b dir="ltr" className="nowrap" style={{ fontSize: '.93rem', color: it.type === 'income' ? 'var(--green)' : 'var(--ink)' }}>{fmt(it.amount, it.type === 'income' ? '+' : '-')}</b>
              </div>
            ))}
          <button className="ubtn ubtn-outline w100 mt10" onClick={() => navigate('Flow')}>לתזרים המלא</button>
        </>
      )}
    </div>
  );
}
